import { useTheme } from '@/components/ThemeProvider';
import { brandMarkFor, MARKETING_SITE_URL } from '@/shared/brand/logos';

/**
 * The wordmark at the top of an authentication screen, which is also the way out.
 *
 * <h2>It leaves the application</h2>
 *
 * This used to be a router `<Link to="/">`, back when `/` rendered a marketing
 * landing page. That page is gone and `/` now redirects straight back to sign-in,
 * so the link would have returned somebody to the screen they were already on.
 *
 * <p>The marketing site is xtremand.com. A plain `<a>` rather than a `<Link>`,
 * because the router has no route for another origin — and the same tab rather
 * than a new one: choosing to read about the product is leaving, not a detour.
 *
 * <p>The ported pages use a `<button>` for the same mark. An anchor is what makes
 * middle-click and "open in a new tab" work, and gives a crawler somewhere to go.
 *
 * <h2>Hover reveals the address; it does not replace the wordmark</h2>
 *
 * This cross-faded the mark out and "← Visit xtremand.com" in. Two objections,
 * one of them from the screen: a logo that vanishes when a pointer crosses it is
 * the one element on the page that cannot be looked at, and the back arrow says
 * "return to where you were", which is wrong — this leaves for another site.
 *
 * <p>So the wordmark stays put and the address appears underneath it. It is
 * always in the flow and only its opacity changes, so revealing it moves
 * nothing; appearing would push the heading below it down by its own height
 * every time a pointer crossed the logo.
 *
 * <p>CSS, not `useState`. `group-hover` does this without a render, and
 * `group-focus-visible` extends it to the keyboard — which a hover-only version
 * never covered, so tabbing to it showed a logo and no indication it was a link.
 */
export function BackToHomeLogo() {
  const { actualTheme } = useTheme();

  return (
    <div className="mb-6 flex justify-center">
      <a
        href={MARKETING_SITE_URL}
        aria-label="Go to xtremand.com"
        className="group relative flex min-w-fit flex-col items-center justify-center rounded-xl px-3 py-3 transition-all duration-300 hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none"
      >
        <span className="relative flex items-center">
          <img src={brandMarkFor(actualTheme)} alt="Xtremand" className="h-12 w-auto" />
        </span>

        {/*
          aria-hidden: the link is already named by aria-label, and without this a
          screen reader reads the wordmark and this address as one name.

          text-[color:var(--xa-accent)] rather than text-blue-600, which the
          ported skin redefines under `.dark` with `!important` -- see CLAUDE.md.
          The token is 5.17:1 on the light ground and 7.22:1 on the dark one.
        */}
        <span
          aria-hidden
          className="mt-1 text-sm text-[color:var(--xa-accent)] opacity-0 transition-opacity duration-300 group-focus-visible:opacity-100 group-hover:opacity-100"
        >
          xtremand.com
        </span>

        <span
          aria-hidden
          className="absolute inset-0 -z-10 rounded-xl bg-gradient-to-r from-blue-50 to-indigo-50 opacity-0 transition-all duration-300 group-hover:opacity-100 group-focus-visible:opacity-100"
        ></span>
      </a>
    </div>
  );
}
